import { useEffect, useId, useState } from "react";
import { flatSrc } from "@/lib/catalog";

const cache = new Map();
const load = (src) => {
  if (!cache.has(src)) {
    cache.set(src, new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    }));
  }
  return cache.get(src);
};

// Pré-carrega um tecido (para a troca ser instantânea).
export const preloadTexture = (t) => load(flatSrc(t)).catch(() => null);

// Imagens já compostas (peça + tecidos): voltar a um padrão já visto é instantâneo.
const composed = new Map();
const decoded = async (url) => {
  const im = new Image();
  im.src = url;
  try { await im.decode(); } catch { /* o navegador decodifica na altura de mostrar */ }
  return url;
};

// Dados (pixéis) de uma máscara, calculados uma só vez.
const dataCache = new Map();
const pixelsOf = (img, w, h, blur = 0) => {
  const k = `${img.src}|${blur}`;
  if (!dataCache.has(k)) {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const x = c.getContext("2d", { willReadFrequently: true });
    if (blur) x.filter = `blur(${blur}px)`; // rebordo mais suave entre o tecido novo e o pelo
    x.drawImage(img, 0, 0, w, h);
    dataCache.set(k, x.getImageData(0, 0, w, h).data);
  }
  return dataCache.get(k);
};

// Bloco de tecido já à escala final e sem emendas: os tecidos planos nem sempre se repetem de forma contínua (o lado esquerdo não
// encaixa no direito, nem o topo na base), e a emenda via-se a cada repetição. Onde o encaixe falha, as margens desse eixo são fundidas para a repetição ser contínua.
const tileCache = new Map();
const seamless = (tex, tw) => {
  const k = `${tex.src}|${tw}`;
  if (tileCache.has(k)) return tileCache.get(k);
  const th = Math.max(1, Math.round((tex.height * tw) / tex.width));
  const c = document.createElement("canvas");
  c.width = tw;
  c.height = th;
  const x = c.getContext("2d", { willReadFrequently: true });
  x.imageSmoothingQuality = "high";
  x.drawImage(tex, 0, 0, tw, th);
  const d = x.getImageData(0, 0, tw, th).data;
  const diff = (i, j) => Math.abs(d[i] - d[j]) + Math.abs(d[i + 1] - d[j + 1]) + Math.abs(d[i + 2] - d[j + 2]);
  let across = 0, near = 0, down = 0, nearV = 0;
  for (let y = 0; y < th; y++) {
    across += diff(y * tw * 4, (y * tw + tw - 1) * 4);
    near += diff(y * tw * 4, (y * tw + 1) * 4);
  }
  for (let xx = 0; xx < tw; xx++) {
    down += diff(xx * 4, ((th - 1) * tw + xx) * 4);
    nearV += diff(xx * 4, (tw + xx) * 4);
  }
  const bad = (a, b, n) => a / n > 12 && a > 2 * b;
  const fx = bad(across, near, th), fy = bad(down, nearV, tw);
  if (fx || fy) {
    // Funde as margens com o próprio bloco deslocado meia volta: nas pontas o desenho vem do meio do bloco, que encaixa em si mesmo.
    const img = x.getImageData(0, 0, tw, th);
    const D = img.data;
    const ramp = (dist, m) => { const t = Math.min(1, dist / m); return t * t * (3 - 2 * t); };
    const blend = (axisX) => {
      const src = new Uint8ClampedArray(D);
      const len = axisX ? tw : th;
      const m = Math.max(2, Math.round(len * 0.22));
      for (let yy = 0; yy < th; yy++) {
        for (let xx = 0; xx < tw; xx++) {
          const u = axisX ? xx : yy;
          const wgt = ramp(Math.min(u, len - 1 - u), m);
          if (wgt >= 1) continue;
          const o = (yy * tw + xx) * 4;
          const q = axisX ? (yy * tw + ((xx + (tw >> 1)) % tw)) * 4 : ((((yy + (th >> 1)) % th) * tw) + xx) * 4;
          for (let ch = 0; ch < 3; ch++) D[o + ch] = src[o + ch] * wgt + src[q + ch] * (1 - wgt);
        }
      }
    };
    if (fx) blend(true);
    if (fy) blend(false);
    x.putImageData(img, 0, 0);
  }
  const out = c;
  tileCache.set(k, out);
  return out;
};

// Fotografia real da peça: o tecido de cada zona é trocado, ao vivo, pelo padrão escolhido (as sombras e pregas são as da fotografia).
// Compõe num canvas fora do ecrã e mostra o resultado dentro de um SVG, que se ajusta sozinho à moldura (e leva o nome personalizado por cima).
export function ChromaPhoto({ spec, textures, label, children, fill = false }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const key = spec.zones.map((z) => textures[z.id] ?? "").join("|");
  const ck = `${spec.dir}|${key}`;
  // Nunca se mostra a fotografia lisa de partida: fica a imagem anterior até a nova estar pronta, sem animação.
  const [src, setSrc] = useState(() => composed.get(ck)?.src ?? null);
  const [bg, setBg] = useState(() => composed.get(ck)?.bg ?? (spec.cutout ? "transparent" : "#B9AC9C"));

  useEffect(() => {
    let cancelled = false;
    const hit = composed.get(ck);
    if (hit) {
      setBg(hit.bg);
      setSrc(hit.src);
      return undefined;
    }
    (async () => {
      try {
        const n = spec.zones.length;
        const [base, ...rest] = await Promise.all([
          load(`${spec.dir}/base.webp?v=${spec.v ?? 1}`),
          ...spec.zones.map((z) => load(`${spec.dir}/${z.id}.png?v=${spec.v ?? 1}`)),
          ...spec.zones.map((z) => load(flatSrc(textures[z.id]))),
          ...(spec.cutout ? [load(`${spec.dir}/cutout.png?v=${spec.v ?? 1}`)] : []),
        ]);
        if (cancelled) return;
        const { width: w, height: h } = base;
        const cv = document.createElement("canvas");
        cv.width = w;
        cv.height = h;
        const ctx = cv.getContext("2d");
        ctx.drawImage(base, 0, 0);
        // Composição que preserva o franzido: o novo tecido é multiplicado pela luminância original (pregas, sombras e realces, já sem o
        // desenho antigo), pixel a pixel, e não por cima de uma sombra desfocada. Valores acima do neutro clareiam (realces).
        const baseData = spec.neutral ? ctx.getImageData(0, 0, w, h) : null;
        spec.zones.forEach((z, i) => {
          if (spec.neutral) {
            const tex = rest[n + i];
            const lc = document.createElement("canvas");
            lc.width = w;
            lc.height = h;
            const tctx = lc.getContext("2d", { willReadFrequently: true });
            const tw = Math.max(8, Math.round(z.tile * w * (textures[z.id]?.startsWith("img:") ? 0.55 : 1)));
            tctx.fillStyle = tctx.createPattern(seamless(tex, tw), "repeat");
            tctx.fillRect(0, 0, w, h);
            const T = tctx.getImageData(0, 0, w, h).data;
            const M = pixelsOf(rest[i], w, h, 0.9);
            const D = baseData.data;
            const gain = z.gain ?? spec.contrast ?? 1.12;
            for (let p = 0; p < D.length; p += 4) {
              const a = M[p + 3] / 255;
              if (a < 0.004) continue;
              const s = 1 + (M[p] / spec.neutral - 1) * gain;
              D[p] = D[p] * (1 - a) + Math.min(255, T[p] * s) * a;
              D[p + 1] = D[p + 1] * (1 - a) + Math.min(255, T[p + 1] * s) * a;
              D[p + 2] = D[p + 2] * (1 - a) + Math.min(255, T[p + 2] * s) * a;
            }
            return;
          }
          const layer = document.createElement("canvas");
          layer.width = w;
          layer.height = h;
          const lc = layer.getContext("2d");
          const tex = rest[n + i];
          const pat = lc.createPattern(tex, "repeat");
          pat.setTransform(new DOMMatrix().scale((z.tile * w * (textures[z.id]?.startsWith("img:") ? 0.55 : 1)) / tex.width));
          lc.fillStyle = pat;
          lc.fillRect(0, 0, w, h);
          lc.globalCompositeOperation = "multiply";
          lc.drawImage(rest[i], 0, 0, w, h);
          lc.globalCompositeOperation = "destination-in";
          lc.drawImage(rest[i], 0, 0, w, h);
          ctx.drawImage(layer, 0, 0);
        });
        if (baseData) ctx.putImageData(baseData, 0, 0);
        if (spec.cutout) {
          // Só a peça (silhueta), sem o fundo da fotografia: assenta no fundo claro da pré-visualização.
          const A = pixelsOf(rest[2 * n], w, h);
          const Z = spec.zones.map((_, i) => pixelsOf(rest[i], w, h, 0.9));
          const out = ctx.getImageData(0, 0, w, h);
          // a zona do tecido conta sempre como peça (a silhueta tem pequenos vazios dentro da gola)
          for (let p = 0; p < out.data.length; p += 4) {
            let a = A[p];
            for (const m of Z) if (m[p + 3] > a) a = m[p + 3];
            out.data[p + 3] = a;
          }
          ctx.putImageData(out, 0, 0);
          const url = await decoded(cv.toDataURL("image/webp", 0.92));
          composed.set(ck, { src: url, bg: "transparent" });
          if (cancelled) return;
          setBg("transparent");
          setSrc(url);
          return;
        }
        // Cor do fundo da própria fotografia, para a moldura se fundir com as margens da imagem.
        const px = (x, y) => ctx.getImageData(x, y, 4, 4).data;
        const pts = [px(4, h - 8), px(Math.floor(w / 2), h - 8), px(w - 8, h - 8), px(w - 8, Math.floor(h * 0.7))]; // margens de baixo: raramente têm adereços
        const c = [0, 1, 2].map((k) => Math.round(pts.reduce((t, d) => t + d[k], 0) / pts.length));
        const url = await decoded(cv.toDataURL("image/jpeg", 0.92));
        const bgc = `rgb(${c.join(",")})`;
        composed.set(ck, { src: url, bg: bgc });
        if (cancelled) return;
        setBg(bgc);
        setSrc(url);
      } catch (err) {
        console.error("ChromaPhoto", err);
        if (!cancelled) setSrc((cur) => cur ?? `${spec.dir}/base.webp?v=${spec.v ?? 1}`);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, ck]);

  const { width: W, height: H } = spec;
  // `focus` recorta a fotografia à peça (o produto ocupa o quadro); sem `focus`, mostra-se a imagem inteira.
  const f = spec.focus ?? { x: 0, y: 0, w: W, h: H };
  return (
    <div className="absolute inset-0" style={{ background: bg }} data-testid="product-designer" data-chroma="1">
      <svg viewBox={`${f.x} ${f.y} ${f.w} ${f.h}`} preserveAspectRatio={fill ? "xMidYMid slice" : "xMidYMid meet"} className="h-full w-full" role="img" aria-label={label}>
        <defs>
          <linearGradient id={`fade${uid}`} gradientUnits="userSpaceOnUse" x1={f.x} x2={f.x + f.w}>
            <stop offset="0" stopColor="#000" /><stop offset="0.06" stopColor="#fff" /><stop offset="0.94" stopColor="#fff" /><stop offset="1" stopColor="#000" />
          </linearGradient>
          <filter id={`sh${uid}`} x="-30%" y="-300%" width="160%" height="700%"><feGaussianBlur stdDeviation="9" /></filter>
          <mask id={`m${uid}`}><rect x={f.x} y={f.y} width={f.w} height={f.h} fill={`url(#fade${uid})`} /></mask>
        </defs>
        {spec.cutout && (
          <ellipse cx={f.x + f.w * 0.5} cy={spec.shadowY ?? 938} rx={f.w * 0.26} ry={f.w * 0.022} fill="rgba(70,52,44,.22)" filter={`url(#sh${uid})`} />
        )}
        {src && <image href={src} width={W} height={H} mask={spec.cutout ? undefined : `url(#m${uid})`} />}
        {children}
      </svg>
    </div>
  );
}
